import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppStore } from "lib/store/useAppStore";
import { getProjectNodeQr } from "lib/api/getFacilityQr";
import { readNodeQrReference, parseFacilityQrMetadata } from "lib/utils/facilityQr";
import FacilityQrMetadata from "components/project/FacilityQrMetadata";
import FacilityQrScanner from "components/project/FacilityQrScanner";
import Spinner from "components/Spinner.jsx";
import FacilityQrHistory from "./FacilityQrHistory";
import "css/facilityQr.css";

function FacilityQrImage({ metadata, online, t, loginReturnPath }) {
  const nodeId = metadata.node_id;
  const [result, setResult] = useState({ state: "loading" });
  useEffect(() => {
    if (!readNodeQrReference(nodeId)) { setResult({ state: "invalid" }); return; }
    if (!online) { setResult({ state: "offline" }); return; }
    const controller = new AbortController();
    setResult({ state: "loading" });
    getProjectNodeQr(nodeId, controller.signal, true).then(response => {
      if (!controller.signal.aborted) setResult(response.success
        ? { state: "ready", ...response.data } : { state: response.status === 401 ? "login" : "error" });
    }).catch(() => { if (!controller.signal.aborted) setResult({ state: "error" }); });
    return () => controller.abort();
  }, [nodeId, online]);
  if (result.state === "login") return <Navigate to="/" replace state={{ from: loginReturnPath }} />;
  if (result.state !== "ready") return <p className="facilityQrStatus" role="status">{
    { loading: "경로와 파일을 불러오는 중…", offline: "인터넷 연결 후 경로를 조회할 수 있습니다.",
      invalid: "Checklab에서 발급한 QR 코드를 스캔해 주세요.", error: "접근 가능한 경로를 불러오지 못했습니다." }[result.state]
  }</p>;
  return <>
    <FacilityQrMetadata metadata={result.metadata} t={t} />
    {result.image_visual && <section className="facilityQrImage"><img src={result.image_visual} alt="선택한 경로의 대표 이미지" /></section>}
    <Link className="facilityQrButton facilityQrButton--secondary facilityQrPathLink" to={`/project/${result.project_id}?${new URLSearchParams({ treeNodeId: result.node_id,
      ...Object.fromEntries([["treeBuildingName", "building_name"], ["treeFloor", "floor"], ["treeLocation", "location"],
        ["treeFacilityName", "facility_name"], ["treePartName", "part_name"]].filter(([, field]) => result.metadata[field])
        .map(([key, field]) => [key, result.metadata[field]])),
    })}`}>이 경로의 파일 보기 · 업로드</Link>
    {!result.can_analyze && <p className="facilityQrNote">선택한 경로의 파일을 오래된순으로 표시합니다. 건물부터 부품까지 지정된 경로에서는 측정 추이도 확인할 수 있습니다.</p>}
    <FacilityQrHistory files={result.files || []} showAnalysis={result.can_analyze} />
  </>;
}

export default function FacilityQrPage() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const status = useAppStore((state) => state.status);
  const [isScanning, setIsScanning] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const metadata = useMemo(() => parseFacilityQrMetadata(search, pathname), [search, pathname]);
  const closeScanner = useCallback(() => setIsScanning(false), []);
  const handleScan = useCallback((path) => {
    setIsScanning(false);
    navigate(path, { replace: true });
  }, [navigate]);
  useEffect(() => {
    const updateOnline = () => setOnline(navigator.onLine !== false);
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);
  if (status === "unknown") return <Spinner />;
  if (status !== "authenticated") {
    return <Navigate to="/" replace state={{ from: `${pathname}${search}` }} />;
  }
  return (
    <main className="facilityQrPage">
      <article className="facilityQrCard">
        <h1>{t("facilityQr.title")}</h1>
        <div className="facilityQrActions">
          <button type="button" className="facilityQrButton" onClick={() => setIsScanning(true)} disabled={isScanning}>
            {t("facilityQr.scanNext")}
          </button>
        </div>
        {isScanning && <FacilityQrScanner t={t} onScan={handleScan} onClose={closeScanner} />}
        <FacilityQrImage key={pathname + search} metadata={metadata} online={online} t={t}
          loginReturnPath={`${pathname}${search}`} />

      </article>
    </main>
  );
}
